import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Building2, Check, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const OrganizationSwitcher = ({ collapsed = false }) => {
  const { organization, organizations, switchOrganization, orgRole } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSwitch = async (orgId) => {
    if (orgId === organization?.id) return;
    
    setIsLoading(true);
    try {
      await switchOrganization(orgId);
      toast.success('Switched organization');
      // Refresh the page to load new org data
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Failed to switch organization');
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-amber-500/20 text-amber-400';
      case 'admin':
        return 'bg-blue-500/20 text-blue-400';
      case 'event_manager':
        return 'bg-green-500/20 text-green-400';
      case 'door_staff':
        return 'bg-purple-500/20 text-purple-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const formatRole = (role) => {
    return role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Member';
  };

  if (!organization) {
    return null;
  }

  // If user only has one organization, show simple display
  if (organizations.length <= 1) {
    return (
      <div className={`flex items-center gap-3 px-3 py-2 ${collapsed ? 'justify-center' : ''}`}>
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-electric-ice-blue to-blue-600 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-4 h-4 text-white" />
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {organization.name}
            </p>
            <p className={`text-xs ${getRoleBadgeColor(orgRole)} inline-block px-1.5 py-0.5 rounded`}>
              {formatRole(orgRole)}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Multiple organizations - show dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`w-full justify-start gap-3 px-3 py-2 h-auto hover:bg-white/5 ${collapsed ? 'justify-center' : ''}`}
          disabled={isLoading}
          data-testid="org-switcher-trigger"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-electric-ice-blue to-blue-600 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-white truncate">
                  {organization.name}
                </p>
                <p className={`text-xs ${getRoleBadgeColor(orgRole)} inline-block px-1.5 py-0.5 rounded`}>
                  {formatRole(orgRole)}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="start" 
        className="w-64 bg-[#1a1a2e] border-white/10"
        data-testid="org-switcher-menu"
      >
        <DropdownMenuLabel className="text-gray-400 text-xs font-normal">
          Switch Organization
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.organization_id}
            onClick={() => handleSwitch(org.organization_id)}
            className="cursor-pointer hover:bg-white/5 focus:bg-white/5"
            data-testid={`org-option-${org.organization_slug}`}
          >
            <div className="flex items-center gap-3 w-full">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-electric-ice-blue/50 to-blue-600/50 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {org.organization_name}
                </p>
                <p className={`text-xs ${getRoleBadgeColor(org.role)} inline-block px-1.5 py-0.5 rounded`}>
                  {formatRole(org.role)}
                </p>
              </div>
              {org.organization_id === organization?.id && (
                <Check className="w-4 h-4 text-electric-ice-blue flex-shrink-0" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onClick={() => navigate('/onboarding')}
          className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-electric-ice-blue"
          data-testid="create-new-org"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New Organization
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OrganizationSwitcher;
